/**
 * `ModulePlaceholderPage` - the screen shown for a platform module that is in
 * scope but has no detailed screens yet.
 *
 * Per the design handoff "Module placeholder screens" section: eyebrow
 * "PLATFORM MODULE" over the module heading, then two columns - the module's
 * capabilities (bulleted with accent dots) beside a dashed striped panel that
 * makes the "designed later" state unmistakable.
 *
 * Copy comes from `module-registry.ts`, which mirrors the MUR Confluence
 * module pages. This component holds no copy of its own beyond the eyebrow
 * and the panel caption.
 *
 * Accessibility:
 *   - The eyebrow is plain text, not a heading, so the heading order stays
 *     h1 -> h2 with no skipped levels.
 *   - Each column is a `<section aria-labelledby>` pointing at its own
 *     heading; the placeholder panel's caption is that section's heading,
 *     rendered visually as a caption.
 *   - The panel is decorative fill behind real text, so the stripe carries
 *     `aria-hidden` and the caption is a normal text node - a screen reader
 *     hears the state without hearing the texture.
 */
import { useId } from 'react'

import type { ModuleDefinition } from './module-registry'

export interface ModulePlaceholderPageProps {
  readonly module: ModuleDefinition
}

export function ModulePlaceholderPage({ module }: ModulePlaceholderPageProps): JSX.Element {
  const id = useId()
  const headingId = `${id}-heading`
  const capabilitiesId = `${id}-capabilities`
  const panelId = `${id}-panel`

  return (
    <div className="flex flex-col gap-murmuration-4">
      <header className="flex flex-col gap-murmuration-1">
        <span className="text-murmuration-eyebrow uppercase text-brand-deep">Platform module</span>
        <h1 id={headingId} tabIndex={-1} className="text-murmuration-h1 text-text-primary">
          {module.title}
        </h1>
      </header>

      <div className="grid grid-cols-1 items-start gap-murmuration-4 lg:grid-cols-2">
        <section
          aria-labelledby={capabilitiesId}
          className="flex flex-col gap-murmuration-3 rounded-md border border-border-default bg-bg-surface p-murmuration-4 shadow-elev-1"
        >
          <h2 id={capabilitiesId} className="text-murmuration-h3 text-text-primary">
            Capabilities
          </h2>
          <p className="text-murmuration-body text-text-secondary">{module.tagline}</p>
          <ul className="flex flex-col gap-murmuration-2">
            {module.capabilities.map((capability) => (
              <li key={capability} className="flex items-start gap-murmuration-2">
                <span
                  aria-hidden="true"
                  className="mt-[7px] h-[6px] w-[6px] flex-none rounded-full bg-brand-primary"
                />
                <span className="text-murmuration-body text-text-primary">{capability}</span>
              </li>
            ))}
          </ul>
          {module.note !== undefined && (
            <p className="text-murmuration-label text-text-muted">{module.note}</p>
          )}
        </section>

        <section
          aria-labelledby={panelId}
          className="flex min-h-[220px] flex-col items-center justify-center gap-murmuration-2 rounded-md border border-dashed border-border-default bg-placeholder-stripe p-murmuration-5 text-center"
        >
          <h2 id={panelId} className="font-mono text-murmuration-code text-text-muted">
            Detailed screens in a later round
          </h2>
          <p className="max-w-[280px] text-murmuration-label text-text-muted">
            This module is in scope. Its queues and detail views are designed in a later round.
          </p>
        </section>
      </div>
    </div>
  )
}
